/**
 * ErrorRecoveryPanel Component
 *
 * Displays error messages with recovery actions when an error occurs
 * during the device update process.
 */

import { Button } from '@ui/shared/Button';
import * as React from 'react';
import styled from 'styled-components';
import { Icon } from '../../../shared/Icon';
import {
  UpdaterError,
  RecoveryAction,
  RecoveryInstruction,
} from '../types/wizard-types';
import { useWizard } from '../hooks/useWizard';
import { HelpFooter } from './HelpFooter';
import { KEYS } from '../../../../keys';

/**
 * Props for ErrorRecoveryPanel
 */
export interface ErrorRecoveryPanelProps {
  error: UpdaterError;
  onRecoveryAction?: (action: RecoveryAction) => void;
}

/**
 * Get icon name for recovery action
 */
const getActionIcon = (action: RecoveryAction): string => {
  switch (action) {
    case RecoveryAction.RETRY_STEP:
      return 'refresh';
    case RecoveryAction.START_OVER:
      return 'restart_alt';
    case RecoveryAction.POWER_CYCLE:
      return 'power_settings_new';
    case RecoveryAction.CHECK_CONNECTION:
      return 'cable';
    case RecoveryAction.CONTACT_SUPPORT:
      return 'support_agent';
    default:
      return 'help';
  }
};

/**
 * Get button flavor for recovery action
 */
const getActionFlavor = (
  action: RecoveryAction,
  isPrimary: boolean
): 'primary' | 'secondary' | 'warning' | 'danger' => {
  if (isPrimary) return 'primary';

  switch (action) {
    case RecoveryAction.START_OVER:
      return 'warning';
    case RecoveryAction.CONTACT_SUPPORT:
      return 'secondary';
    default:
      return 'secondary';
  }
};

/**
 * ErrorRecoveryPanel component
 */
export const ErrorRecoveryPanel: React.FC<ErrorRecoveryPanelProps> = ({
  error,
  onRecoveryAction,
}: ErrorRecoveryPanelProps): React.ReactElement => {
  const { retry, reset } = useWizard();

  // Sort recovery actions by order
  const sortedActions = [...error.recoveryActions].sort(
    (a: RecoveryInstruction, b: RecoveryInstruction): number =>
      a.order - b.order
  );

  /**
   * Handle recovery action click
   */
  const handleAction = (instruction: RecoveryInstruction): void => {
    // Call custom handler if provided
    if (onRecoveryAction) {
      onRecoveryAction(instruction.action);
    }

    // Execute built-in actions
    switch (instruction.action) {
      case RecoveryAction.RETRY_STEP:
        retry();
        break;
      case RecoveryAction.START_OVER:
        reset();
        break;
      // Other actions may require custom handling
      default:
        break;
    }
  };

  return (
    <ErrorContainer data-testid={KEYS.WIZARD_ERROR_PANEL}>
      <ErrorHeader>
        <ErrorIconWrapper>
          <Icon name='error' color='white' size='small' />
        </ErrorIconWrapper>
        <ErrorTitle>Update Error</ErrorTitle>
      </ErrorHeader>

      <ErrorMessage>{error.message}</ErrorMessage>

      {error.technicalDetails && (
        <TechnicalDetails>
          <DetailsToggle>
            <DetailsIconWrapper>
              <Icon name='info' size='small' />
            </DetailsIconWrapper>
            <span>Technical Details</span>
          </DetailsToggle>
          <DetailsContent>{error.technicalDetails}</DetailsContent>
        </TechnicalDetails>
      )}

      <RecoverySection>
        <RecoveryActions>
          {sortedActions.map(
            (
              instruction: RecoveryInstruction,
              index: number
            ): React.ReactNode => {
              const flavor = getActionFlavor(instruction.action, index === 0);
              const isWarning = flavor === 'warning';
              return (
                <ActionButton
                  key={instruction.action}
                  icon={getActionIcon(instruction.action)}
                  color={isWarning ? 'black' : undefined}
                  title={instruction.label}
                  flavor={flavor}
                  onClick={(): void => handleAction(instruction)}
                  $isWarning={isWarning}
                />
              );
            }
          )}
        </RecoveryActions>
        <ActionDescriptions>
          {sortedActions.map(
            (instruction: RecoveryInstruction): React.ReactNode => (
              <ActionDescription key={instruction.action}>
                <strong>{instruction.label}:</strong> {instruction.description}
              </ActionDescription>
            )
          )}
        </ActionDescriptions>
      </RecoverySection>

      <ErrorCode data-testid={KEYS.WIZARD_ERROR_CODE}>
        Error Code: {error.code}
      </ErrorCode>

      <HelpFooter />
    </ErrorContainer>
  );
};

// Styled Components

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--color-surface-1);
  border: 2px solid var(--color-react-select-danger);
  border-radius: 10px;
  padding: 20px;
  margin: 20px 0;
`;

const ErrorHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const ErrorIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--color-react-select-danger);
  margin-right: 16px;
`;

const ErrorTitle = styled.h3`
  margin: 0;
  color: var(--color-react-select-danger);
  font-size: 20px;
  font-weight: bold;
`;

const ErrorMessage = styled.p`
  margin: 0 0 16px 0;
  color: var(--color-text);
  font-size: 16px;
  line-height: 1.5;
`;

const TechnicalDetails = styled.details`
  margin-bottom: 16px;
  border: 1px solid var(--color-separator);
  border-radius: 6px;
  overflow: visible;
`;

const DetailsToggle = styled.summary`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: var(--color-surface-2);
  cursor: pointer;
  font-size: 13px;
  color: var(--color-text);
  list-style: none;
  overflow: visible;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    background: var(--color-surface-3);
  }
`;

const DetailsIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
`;

const DetailsContent = styled.pre`
  margin: 0;
  padding: 12px;
  background: var(--color-surface-0);
  font-size: 12px;
  font-family: monospace;
  color: var(--color-text);
  white-space: pre-wrap;
  word-break: break-all;
`;

const RecoverySection = styled.div`
  margin-bottom: 16px;
`;

const RecoveryActions = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
`;

const ActionButton = styled(Button)<{ flavor?: string; $isWarning?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  padding: 12px 16px;
  min-height: 44px;
  overflow: visible;

  /* Dark text for warning buttons */
  ${(props: { flavor?: string; $isWarning?: boolean }): string =>
    props.$isWarning
      ? `
    color: #1a1a1a;
  `
      : ''}
`;

const ActionDescriptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
`;

const ActionDescription = styled.span`
  font-size: 13px;
  color: var(--color-text);
`;

const ErrorCode = styled.span`
  font-size: 12px;
  color: var(--color-text);
  font-family: monospace;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--color-separator);
`;

export default ErrorRecoveryPanel;
